import { useEffect, useRef } from 'react';
import { useLocation, Link, Outlet } from 'react-router';
import { FromPathnameContext } from '@/lib/navigation-context';
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarHeader,
	SidebarInset,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarProvider,
	SidebarTrigger,
	useSidebar
} from '@/components/ui/sidebar';
import { menuItems, type MenuItem } from '@/config/menu.config';
import { useCanEditInternals } from '@/hooks/use-developer-mode';
import { useIntegrations } from '@/hooks';
import { BackgroundJobPoller } from '@/components/background-job-poller';
import { SentinelAssignmentAlerts } from '@/components/sentinel-assignment-alerts';
import { AbilityContext } from '@/lib/ability-context';
import { getLandingPath, hasDedicatedLanding, SENTINEL_WORKSPACE_PATH } from '@/lib/landing';
import { usePermissions } from '@/hooks/use-permissions';
import { useAccountFlags } from '@/hooks/use-account-flags';
import { useSentinelDashboardEnabled } from '@/hooks/use-sentinel-dashboard-enabled';
import { SidebarUserMenu } from './sidebar-user-menu';
import { CommandPalette } from '@/components/command-palette';

export default function AppLayout() {
	const location = useLocation();
	useIntegrations();
	const { ability, isSuperAdmin, role } = usePermissions();
	const { flags } = useAccountFlags();
	const canEditInternals = useCanEditInternals();
	const landingPath = getLandingPath(role);

	// Items without a subject (the "Dashboard" home entry) are open to everyone
	// except roles pinned to a single dashboard — those get only what their
	// permissions explicitly name, since the home page redirects them away.
	const showUnscopedItems = isSuperAdmin || !hasDedicatedLanding(role);
	const isVisible = (subject?: string) =>
		subject ? isSuperAdmin || ability.can('view', subject) : showUnscopedItems;

	// A flag the account has not switched on hides the entry for everyone, super admins included.
	// Permissions describe what a role MAY see; this describes what exists for the account, and a
	// super admin looking at an account without the feature should not see a page that cannot work.
	// Absent while the flags are still loading, so the entry appears when confirmed rather than
	// flashing and being taken away.
	const flagAllows = (flag?: string) => !flag || flags[flag] === true;

	// With Sentinel off the workspace is just an "it's turned off" page, so hide it.
	const sentinelDashboardEnabled = useSentinelDashboardEnabled();

	// THREE independent filters, unioned at the merge: the account flag (this branch), the Sentinel
	// switch and the devOnly gate (develop). They answer different questions — does the account have
	// the feature, is this particular page worth showing, and may this user see internals — so an
	// item has to pass all three rather than whichever arrived last.
	const visibleMenu = menuItems
		.map((item) => {
			if (!flagAllows(item.flag)) return null;
			if (role && item.hiddenForRoles?.includes(role)) return null;
			if (item.path === SENTINEL_WORKSPACE_PATH && sentinelDashboardEnabled === false) return null;

			if (!item.subItems || item.subItems.length === 0) {
				return isVisible(item.subject) ? item : null;
			}
			const subs = item.subItems.filter(
				(s) =>
					(s.devOnly ? canEditInternals : true) &&
					(s.superAdminOnly ? isSuperAdmin : isVisible(s.subject)) &&
					flagAllows(s.flag)
			);
			// Hide parent when no children pass AND parent has no own subject
			// (or its own subject is also denied).
			const parentSelfVisible = item.subject ? isVisible(item.subject) : false;
			if (subs.length === 0 && !parentSelfVisible) return null;
			// The icon links to the first page this user can actually open.
			return { ...item, path: subs[0]?.path ?? item.path, subItems: subs };
		})
		.filter((x): x is MenuItem => x !== null);

	const prevPathnameRef = useRef(location.pathname);
	const fromPathname = prevPathnameRef.current;

	useEffect(() => {
		prevPathnameRef.current = location.pathname;
	}, [location.pathname]);

	// Check if a route is active
	const isRouteActive = (item: MenuItem) => {
		const currentPath = location.pathname;
		if (item.activePrefix) return currentPath.startsWith(item.activePrefix);
		
		// Exact match for the main route
		if (item.path === currentPath) return true;
		if (item.subItems) {
			return item.subItems.some((s) => currentPath.startsWith(s.path));
		}

		return false;
	};

	return (
		<FromPathnameContext.Provider value={fromPathname}>
			<AbilityContext.Provider value={ability}>
			<SidebarProvider defaultOpen={false}>
				<Sidebar collapsible='icon'>
					<SidebarHeader>
						<Link to={landingPath}>
							<div className='flex items-center gap-2 cursor-pointer'>
								<div className='flex shrink-0 items-center justify-center rounded-lg text-primary-foreground'>
									<img src='/assets/brand/kim-ai-logo.svg' className='h-9 w-9' alt='Kim AI Logo' />
								</div>
							</div>
						</Link>
					</SidebarHeader>
					<SidebarContent>
						<SidebarMenu>
							{visibleMenu.map((item) => {
								const isActive = isRouteActive(item);

								return (
									<SidebarMenuItem key={item.path}>
										<SidebarMenuButton asChild tooltip={item.label} isActive={isActive}>
											<Link to={item.path}>
												<item.icon />
												<span>{item.label}</span>
											</Link>
										</SidebarMenuButton>
									</SidebarMenuItem>
								);
							})}
						</SidebarMenu>
					</SidebarContent>
					<SidebarFooter className='px-0 pb-3'>
						<SidebarUserMenu showAccountSwitcher />
					</SidebarFooter>
				</Sidebar>
				<SidebarInset>
					<MobileTopBar landingPath={landingPath} />
					<div className='flex min-h-0 flex-1 flex-col p-0'>
						<Outlet />
					</div>
					<BackgroundJobPoller />
					<SentinelAssignmentAlerts />
					<CommandPalette items={visibleMenu} />
				</SidebarInset>
			</SidebarProvider>
			</AbilityContext.Provider>
		</FromPathnameContext.Provider>
	);
}

/**
 * Phones only: the sidebar is a sheet there, and this bar is the way to open it. It closes again
 * once a page is picked, so the page is what shows.
 */
function MobileTopBar({ landingPath }: { landingPath: string }) {
	const { setOpenMobile } = useSidebar();
	const { pathname } = useLocation();
	useEffect(() => setOpenMobile(false), [pathname, setOpenMobile]);
	return (
		<header className='sticky top-0 z-20 flex h-12 shrink-0 items-center gap-2 border-b border-gray-300 bg-white px-2 md:hidden'>
			<SidebarTrigger aria-label='Open menu' className='size-9' />
			<Link to={landingPath} aria-label='Home'>
				<img src='/assets/brand/kim-ai-logo.svg' className='h-7 w-7' alt='' />
			</Link>
		</header>
	);
}
